import axios, { type AxiosError, type InternalAxiosRequestConfig } from 'axios';

import { useSettingsStore } from '@/store/useSettingsStore';
import useUser, { USER_INIT_STATE } from '@/store/useUser';

export interface IApiErrorData {
  message: string;
}

const STATIC_TOKEN = import.meta.env.VITE_APP_STATIC_TOKEN;

// Create a map to store the AbortController instances
const abortControllers = new Map<string, AbortController>();

// Create a function to generate a unique token for each request
const generateRequestToken = (config: InternalAxiosRequestConfig): string => {
  const { method, url, params, data } = config;
  return `${String(method)}-${String(url)}-${JSON.stringify(params)}-${JSON.stringify(data)}`;
};

// Create instance of axios
const http = axios.create({
  baseURL: import.meta.env.VITE_APP_API_ENDPOINT,
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  },
});

// Create a request interceptor for the instance and get accessToken
http.interceptors.request.use(
  async (config) => {
    // Attach an AbortController to the request
    const requestToken = generateRequestToken(config);
    const abortController = new AbortController();
    abortControllers.set(requestToken, abortController);
    config.signal = abortController.signal;
    // Set timeout for the request
    config.timeout = 50000;

    const { user } = useUser.getState();
    const { settings } = useSettingsStore.getState();

    // Set Authorization header
    const token = user?.token || STATIC_TOKEN;

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Set accept-language header dynamically from settings store
    config.headers['accept-language'] = settings.language || 'en';
    config.headers['x-device-type'] = 1;
    return config;
  },
  async (error) => {
    return await Promise.reject(error);
  }
);

http.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<IApiErrorData>) => {
    if (axios.isAxiosError(error) && error.response) {
      if (error?.response.status === 401) {
        localStorage.clear();
        useUser.setState({
          user: USER_INIT_STATE,
        });
        window.location.reload();
        return '';
      } else if (error?.response?.status === 500) {
        console.error(error?.response?.data?.message);
      }
      throw error;
    }
  }
);

// Create a function to cancel a request using the associated AbortController
export const cancelRequest = (config: InternalAxiosRequestConfig): void => {
  const requestToken = generateRequestToken(config);
  const abortController = abortControllers.get(requestToken);
  if (abortController) {
    abortController.abort();
    abortControllers.delete(requestToken);
  }
};

export default http;
